import React, { Component } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Grid, Cell } from 'react-flexr';

import Container from 'app/common/narrow-container';
import Button from 'app/common/button';

import Paper from '@mui/material/Paper';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import FormInput from 'app/common/form/input';
import CheckboxDialog from 'app/common/CheckboxDialog';

import { toastr } from 'app/common/toastr';

import { signup, SIGNUP_SUCCESS } from 'app/redux/modules/auth';

import i18n from 'app/common/i18n';

@connect(({ auth: { user } }) => ({ user }), { signup })
@i18n
class Signup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 1,
      loading: false,
      errors: {},
      error: null,
      valid: null,
      type: ''
    };
  }

  error(error, state = {}) {
    toastr.error(this.props.i18n('New landlord'), error);
    return this.setState({ error, ...state });
  }

  warning(state = { errors: {} }) {
    return this.setState({
      errors: !state.errors?.clear
        ? Object.assign(this.state.errors, state.errors)
        : {}
    });
  }

  validate(targetName) {
    let valid = null;

    const {
      company,
      type,
      firstname,
      lastname,
      phone,
      email,
      password,
      password2,
      terms
    } = this.state;

    if (
      ((type === 'BUSINESS' && company) || type === 'PERSONAL') &&
      firstname &&
      lastname &&
      phone &&
      email &&
      password &&
      password.length >= 6 &&
      password === password2 &&
      terms
    ) {
      valid = true;
    }

    this.warning({
      errors: { clear: false }
    });

    switch (targetName) {
      case 'phone':
        this.warning({
          errors: {
            phone: !phone ? this.props.i18n('Missing phone') : null
          }
        });
        break;

      case 'firstname':
        this.warning({
          errors: {
            firstname: !firstname ? this.props.i18n('Missing firstname') : null
          }
        });
        break;

      case 'lastname':
        this.warning({
          errors: {
            lastname: !lastname ? this.props.i18n('Missing lastname') : null
          }
        });
        break;

      case 'type':
        this.warning({
          errors: {
            type: !type
              ? this.props.i18n(
                  '%s must be filled',
                  this.props.i18n('Landlord type')
                )
              : null
          }
        });
        break;

      case 'email':
        if (!email) {
          this.warning({
            errors: {
              email: this.props.i18n('No email')
            }
          });
        } else if (!/^\S+@\S+.\S+$/.test(email)) {
          this.warning({
            errors: {
              email: this.props.i18n('Email is not valid')
            }
          });
        } else {
          this.warning({
            errors: { email: null }
          });
        }
        break;

      case 'password':
      case 'password2':
        if (!password || !password2) {
          this.warning({
            errors: {
              password2: this.props.i18n('Password is missing')
            }
          });
        } else if (password !== password2) {
          this.warning({
            errors: {
              password2: this.props.i18n('Passwords do not match')
            }
          });
        } else {
          this.warning({
            errors: { password2: null }
          });
        }
        break;

      case 'company':
        this.warning({
          errors: {
            company:
              !company && type === 'BUSINESS'
                ? this.props.i18n('No company name')
                : null
          }
        });
        break;

      default:
        break;
    }

    if (valid) {
      this.warning({
        errors: { clear: true }
      });
    }

    this.setState({ valid });
  }

  handleSubmit = (ev) => {
    ev.preventDefault();

    const {
      company,
      companyAddress,
      type,
      firstname,
      lastname,
      phone,
      email,
      password,
      password2,
      cvr,
      terms
    } = this.state;

    if (!type) {
      const error = this.props.i18n(
        '%s must be filled',
        this.props.i18n('Landlord type')
      );
      return this.error(error, { errors: { type: error } });
    }

    if (!email) {
      return this.error(this.props.i18n('No email'));
    }

    if (!/^\S+@\S+.\S+$/.test(email)) {
      return this.error(this.props.i18n('Email is not valid'));
    }

    if (!password || !password2) {
      return this.error(this.props.i18n('Password is missing'));
    }

    if (password !== password2) {
      return this.error(this.props.i18n('Passwords do not match'), {
        errors: { password2: this.props.i18n('Passwords do not match') }
      });
    }

    if (!company && type === 'BUSINESS') {
      return this.error(this.props.i18n('No company name'));
    }

    if (!terms) {
      return this.error(this.props.i18n('Mangler at accepter vilkår'));
    }

    this.setState({ error: null, loading: true });

    this.props
      .signup({
        type,
        company,
        companyAddress,
        firstname,
        lastname,
        phone,
        email,
        password,
        cvr
      })
      .then((action) => {
        if (action.type === SIGNUP_SUCCESS) {
          toastr.success(
            this.props.i18n('New landlord'),
            this.props.i18n('User has been created')
          );

          return this.props.navigate('/dwellings/create', {
            replace: true
          });
        }

        const { error: { response } = {} } = action || {};

        if (response && response.message === 'User validation failed') {
          const errors = {};

          if (response.errors) {
            Object.keys(response.errors).forEach((field) => {
              errors[field] = this.props.i18n(response.errors[field].message);
            });
          }

          this.error(this.props.i18n('User creation failed'), {
            errors,
            loading: false
          });

          return action;
        }

        this.error(this.props.i18n(response && response.message), {
          loading: false
        });

        return action;
      });
  };

  onChange = (ev) => {
    const name = ev.target.name;

    this.setState(
      {
        [name]:
          ev.target.type === 'checkbox' ? ev.target.checked : ev.target.value
      },
      () => this.validate(name)
    );
  };

  step() {
    switch (this.state.step) {
      case 1: {
        const { errors, loading, valid } = this.state;

        return (
          <div>
            <FormControl
              fullWidth
              required
              style={{ marginBottom: 24 }}
              error={Boolean(errors.type)}
            >
              <InputLabel htmlFor="type" shrink>
                {this.props.i18n('Landlord type')}
              </InputLabel>
              <Select
                displayEmpty
                autoWidth
                onChange={this.onChange}
                value={this.state.type}
                input={<Input name="type" />}
              >
                <MenuItem value="PERSONAL">
                  {this.props.i18n('Personal')}
                </MenuItem>
                <MenuItem value="BUSINESS">
                  {this.props.i18n('Professional')}
                </MenuItem>
              </Select>
              {errors.type ? (
                <FormHelperText>{errors.type}</FormHelperText>
              ) : null}
            </FormControl>

            {this.state.type === 'BUSINESS' && (
              <>
                <Grid style={{ marginBottom: 0 }}>
                  <Cell>
                    <FormInput
                      name="company"
                      type="text"
                      label={this.props.i18n('Company name')}
                      defaultValue={this.state.company}
                      errorText={errors.company}
                      required
                      onChange={this.onChange}
                      fullWidth
                    />
                  </Cell>
                  <Cell>
                    <FormInput
                      name="cvr"
                      type="text"
                      label={this.props.i18n('CVR')}
                      defaultValue={this.state.cvr}
                      errorText={errors.cvr}
                      onChange={this.onChange}
                      fullWidth
                    />
                  </Cell>
                </Grid>
                <Grid style={{ marginBottom: 0 }}>
                  <Cell>
                    <FormInput
                      name="companyAddress"
                      type="text"
                      label={this.props.i18n('Company address')}
                      defaultValue={this.state.companyAddress}
                      errorText={errors.companyAddress}
                      onChange={this.onChange}
                      fullWidth
                    />
                  </Cell>
                </Grid>
              </>
            )}

            <Grid style={{ marginBottom: 0 }}>
              <Cell>
                <FormInput
                  name="firstname"
                  type="text"
                  label={this.props.i18n('Given name(s)')}
                  errorText={errors.firstname}
                  required
                  onChange={this.onChange}
                  fullWidth
                />
              </Cell>
              <Cell>
                <FormInput
                  name="lastname"
                  type="text"
                  label={this.props.i18n('Family name')}
                  errorText={errors.lastname}
                  required
                  onChange={this.onChange}
                  fullWidth
                />
              </Cell>
            </Grid>

            <FormInput
              name="phone"
              type="tel"
              label={this.props.i18n('Phone')}
              errorText={errors.phone}
              required
              onChange={this.onChange}
              fullWidth
            />
            <FormInput
              name="email"
              type="email"
              label={this.props.i18n('Email')}
              errorText={errors.email}
              required
              onChange={this.onChange}
              fullWidth
            />

            <Grid style={{ marginBottom: 0 }}>
              <Cell>
                <FormInput
                  name="password"
                  type="password"
                  label={this.props.i18n('Password')}
                  helperText={this.props.i18n(
                    'At least 6 characters, digits or symbols'
                  )}
                  errorText={errors.password}
                  required
                  onChange={this.onChange}
                  fullWidth
                />
              </Cell>
              <Cell>
                <FormInput
                  name="password2"
                  type="password"
                  label={this.props.i18n('Password (again)')}
                  errorText={errors.password2}
                  required
                  onChange={this.onChange}
                  fullWidth
                />
              </Cell>
            </Grid>

            <Grid>
              <Cell>
                <CheckboxDialog
                  name="terms"
                  error={errors.terms}
                  removeError={() => {}}
                  onChange={this.onChange}
                  label={({ handleOpen }) => {
                    return (
                      <>
                        {this.props.i18n(
                          "Jeg har læst og accepterer Akutbolig.dk's udlejerplatform generelle"
                        )}{' '}
                        <a href="#" onClick={handleOpen}>
                          {this.props.i18n('vilkår og persondatapolitik')}
                        </a>
                      </>
                    );
                  }}
                  getContent={() => {
                    const Terms = React.lazy(() =>
                      import(/* webpackPrefetch: true */ '../terms.mdx')
                    );
                    const PrivacyPolicy = React.lazy(() =>
                      import(
                        /* webpackPrefetch: true */ '../privacy-policy.mdx'
                      )
                    );

                    return (
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Terms />
                        <hr />
                        <PrivacyPolicy />
                      </React.Suspense>
                    );
                  }}
                />
              </Cell>
            </Grid>

            <div style={{ display: 'flex', marginTop: '1em' }}>
              <Button
                type="submit"
                primary
                disabled={loading || !valid}
                label={this.props.i18n('Create user')}
              />
              <Button
                to="/"
                primary
                flat
                label={this.props.i18n('Already got a user?')}
                style={{ marginLeft: 8 }}
              />
            </div>
          </div>
        );
      }

      default:
        return '';
    }
  }

  render() {
    return (
      <Container header={this.props.i18n('New landlord')}>
        <Paper
          component="form"
          className="signup"
          onSubmit={this.handleSubmit}
          style={{ marginBottom: '1em', padding: '1em' }}
        >
          {this.step()}
        </Paper>
      </Container>
    );
  }
}

function SignupWithNavigate(props) {
  const navigate = useNavigate();
  return <Signup {...props} navigate={navigate} />;
}

export default SignupWithNavigate;